import { CheckCircle, XCircle, AlertTriangle } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import Gauge from "./gauge"
import { CandidateSummary } from "openapi/api/model"
import CandidateAnalysis from "./decision-details"
import { DecisionMakingSummary } from 'openapi/api/model/decisionMakingSummary';


export type DecisionSummaryCardProps = {
  summary?: DecisionMakingSummary
  decision: string
  reasoning: string,
  candidate: CandidateSummary
}


const formatScore = (score: number) => {
  return Math.round(score * 100)
}

const formatExperience = (years: number) => {
  return years.toFixed(1)
}

export default function DecisionSummaryCard({ summary, decision, reasoning, candidate }: DecisionSummaryCardProps) {



  const getDecisionColor = (decision: string) => {
    switch (decision) {
      case "Super Candidate":
        return "text-green-500"
      case "Top Candidate":
        return "text-blue-500"
      case "Full Evaluation":
        return "text-yellow-500"
      case "No Match":
      case "Failed Pre-qualification":
        return "text-red-500"
      default:
        return "text-gray-500"
    }
  }

  const getDecisionIcon = (decision: string) => {
    switch (decision) {
      case "Super Candidate":
      case "Top Candidate":
        return <CheckCircle className="w-6 h-6" />
      case "Full Evaluation":
        return <AlertTriangle className="w-6 h-6" />
      case "No Match":
      case "Failed Pre-qualification":
        return <XCircle className="w-6 h-6" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <div className={`flex items-center space-x-2 ${getDecisionColor(decision)} font-bold text-lg`}>
        {getDecisionIcon(decision)}
        <span>{decision}</span>
      </div>
      {decision === "Full Evaluation" && (
        <div className="text-yellow-500 font-semibold">Technical assessment recommended</div>
      )}
      <CandidateAnalysis candidate={candidate} decision={decision} reasoning={reasoning} summary={summary}/>
      
      { summary &&(
        <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Gauge value={formatScore(summary.totalProblemSpaceScore)} label="Problem Space Score" size="sm" />
        <Gauge value={formatScore(summary.niceToHave_skill_score)} label="Nice-to-Have Skills" size="sm" />
        <div>
          <h3 className="font-semibold mb-1">Experience Match</h3>
          {summary.match_experience ? (
            <div className="flex items-center space-x-2 text-green-500">
              <CheckCircle className="w-5 h-5" />
              <span>Matches required experience</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-red-500">
              <XCircle className="w-5 h-5" />
              <span>Does not match required experience</span>
            </div>
          )}
        </div>
      </div>
      <div>
        <h3 className="font-semibold mb-1">Total Experience</h3>
        <p>{formatExperience(summary.total_experience_years)} years</p>
      </div>
      <div>
        <h3 className="font-semibold mb-1">Main Tech Specialty Experience</h3>
        <p>{formatExperience(summary.mainTechSpecialtyExperience)} years</p>
      </div>
      <div>
        <h3 className="font-semibold mb-1">Tags</h3>
        <div className="flex flex-wrap gap-2">
          {summary.tags.map((tag, index) => (
            <Badge key={index} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      </div>
        </>
      )}

    </div>
  )
}
